import { useFrame, useThree } from '@react-three/fiber'
import { useEffect, useRef } from 'react'
import {
    BufferAttribute,
    BufferGeometry,
    Color,
    NormalBlending,
    Points,
    PointsMaterial,
    Quaternion,
    Vector3,
} from 'three'
import { useFootstepEffectTextures } from '../../effects/useFootstepEffectTextures'

export type ObstacleDestroyBurstConfig = {
    center: Vector3
    halfExtents: Vector3
    rotation: Quaternion
}

type ObstacleDestroyBurst = {
    age: number
    geometry: BufferGeometry
    material: PointsMaterial
    points: Points
    positions: Float32Array
    velocities: Float32Array
}

const obstacleDestroySmokeSettings = {
    color: '#f4f4f4',
    count: 28,
    size: 0.85,
    endSize: 2.8,
    opacity: 0.8,
    lifetime: 0.42,
    drag: 7.5,
    speed: 1.9,
    upwardDrift: 0.45,
    outwardVelocity: 0.65,
    randomVelocity: 0.32,
}

type ObstacleDestroyEffectProps = {
    consumeBurstQueue: () => ObstacleDestroyBurstConfig[]
}

const _localPosition = new Vector3()
const _worldPosition = new Vector3()
const _velocity = new Vector3()
const _boxNormalizer = new Vector3()

export function ObstacleDestroyEffect({
    consumeBurstQueue,
}: ObstacleDestroyEffectProps) {
    const { scene } = useThree()
    const activeBurstsRef = useRef<ObstacleDestroyBurst[]>([])
    const { dustTexture } = useFootstepEffectTextures()

    useEffect(() => {
        return () => {
            for (const burst of activeBurstsRef.current) {
                scene.remove(burst.points)
                burst.geometry.dispose()
                burst.material.dispose()
            }
        }
    }, [scene])

    function spawnBurst(config: ObstacleDestroyBurstConfig) {
        const count = Math.max(1, Math.floor(obstacleDestroySmokeSettings.count))
        const positions = new Float32Array(count * 3)
        const velocities = new Float32Array(count * 3)
        const geometry = new BufferGeometry()
        geometry.setAttribute('position', new BufferAttribute(positions, 3))

        const safeHalfExtents = _boxNormalizer.set(
            Math.max(config.halfExtents.x, 0.18),
            Math.max(config.halfExtents.y, 0.18),
            Math.max(config.halfExtents.z, 0.18),
        )

        for (let index = 0; index < count; index += 1) {
            const i3 = index * 3

            _localPosition.set(
                (Math.random() * 2 - 1) * safeHalfExtents.x,
                (Math.random() * 2 - 1) * safeHalfExtents.y,
                (Math.random() * 2 - 1) * safeHalfExtents.z,
            )

            _worldPosition.copy(_localPosition).applyQuaternion(config.rotation)
            positions[i3] = _worldPosition.x
            positions[i3 + 1] = _worldPosition.y
            positions[i3 + 2] = _worldPosition.z

            _velocity.copy(_worldPosition).normalize().multiplyScalar(
                obstacleDestroySmokeSettings.outwardVelocity * (0.65 + Math.random() * 0.7),
            )
            _velocity.x += (Math.random() * 2 - 1) * obstacleDestroySmokeSettings.randomVelocity
            _velocity.y += obstacleDestroySmokeSettings.upwardDrift + Math.random() * 0.25
            _velocity.z += (Math.random() * 2 - 1) * obstacleDestroySmokeSettings.randomVelocity

            velocities[i3] = _velocity.x
            velocities[i3 + 1] = _velocity.y
            velocities[i3 + 2] = _velocity.z
        }

        const material = new PointsMaterial({
            color: new Color(obstacleDestroySmokeSettings.color),
            size: obstacleDestroySmokeSettings.size,
            alphaMap: dustTexture,
            transparent: true,
            opacity: obstacleDestroySmokeSettings.opacity,
            depthWrite: false,
            blending: NormalBlending,
            sizeAttenuation: true,
            toneMapped: false,
        })

        const points = new Points(geometry, material)
        points.position.copy(config.center)
        scene.add(points)

        activeBurstsRef.current.push({
            age: 0,
            geometry,
            material,
            points,
            positions,
            velocities,
        })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 30)
        const queuedBursts = consumeBurstQueue()
        for (const burst of queuedBursts) {
            spawnBurst(burst)
        }

        for (let index = activeBurstsRef.current.length - 1; index >= 0; index -= 1) {
            const burst = activeBurstsRef.current[index]
            burst.age += safeDelta
            const t = burst.age / obstacleDestroySmokeSettings.lifetime

            if (t >= 1) {
                scene.remove(burst.points)
                burst.geometry.dispose()
                burst.material.dispose()
                activeBurstsRef.current.splice(index, 1)
                continue
            }

            const drag = Math.exp(-obstacleDestroySmokeSettings.drag * safeDelta)

            for (let p = 0; p < burst.positions.length; p += 3) {
                burst.positions[p] += burst.velocities[p] * obstacleDestroySmokeSettings.speed * safeDelta
                burst.positions[p + 1] += burst.velocities[p + 1] * obstacleDestroySmokeSettings.speed * safeDelta
                burst.positions[p + 2] += burst.velocities[p + 2] * obstacleDestroySmokeSettings.speed * safeDelta

                burst.velocities[p] *= drag
                burst.velocities[p + 1] *= drag
                burst.velocities[p + 2] *= drag
            }

            burst.geometry.attributes.position.needsUpdate = true
            burst.material.opacity =
                obstacleDestroySmokeSettings.opacity *
                Math.pow(1 - t, 1.6)
            burst.material.size =
                obstacleDestroySmokeSettings.size +
                (obstacleDestroySmokeSettings.endSize - obstacleDestroySmokeSettings.size) *
                t
        }
    })

    return null
}

